import React, { useState } from 'react';
import { useNavigate, Link, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Mail, Lock, Palmtree } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();

  const redirectParam = searchParams.get('redirect');
  const from = redirectParam || location.state?.from?.pathname || '/calculator';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(formData.email, formData.password);
      toast.success('Connexion réussie!');
      navigate(from, { replace: true });
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Email ou mot de passe incorrect');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-warm-sand flex items-center justify-center py-12 px-6">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Palmtree className="w-10 h-10 text-[#005F73]" strokeWidth={1.5} />
            <span className="text-2xl font-semibold text-[#005F73]">GwadImmo Invest</span>
          </div>
          <h1 className="text-3xl font-semibold text-[#005F73] mb-2">Connexion</h1>
          <p className="text-stone-600">Accédez à vos projets immobiliers</p>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl border border-stone-100 p-8 shadow-[0_4px_20px_-4px_rgba(0,95,115,0.1)]">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="email">Email</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-3 w-5 h-5 text-stone-400" strokeWidth={1.5} />
                <Input
                  data-testid="input-email"
                  id="email"
                  type="email"
                  required
                  placeholder="vous@exemple.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="pl-10 h-12 rounded-xl"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password">Mot de passe</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-3 w-5 h-5 text-stone-400" strokeWidth={1.5} />
                <Input
                  data-testid="input-password"
                  id="password"
                  type="password"
                  required
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="pl-10 h-12 rounded-xl"
                />
              </div>
            </div>

            <Button
              data-testid="btn-login"
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-xl bg-[#005F73] hover:bg-[#005F73]/90 text-lg font-medium"
            >
              {loading ? 'Connexion...' : 'Se connecter'}
            </Button>
          </form>

          <div className="mt-4 text-center">
            <Link 
              to="/forgot-password" 
              className="text-sm text-[#005F73] hover:underline font-medium"
            >
              Mot de passe oublié ?
            </Link>
          </div>

          <div className="mt-6 text-center text-sm text-stone-600">
            Pas encore de compte?{' '}
            <Link to="/register" className="text-[#005F73] font-medium hover:underline">
              Créer un compte
            </Link>
          </div>
        </div>

        {/* Link to home */}
        <div className="mt-6 text-center">
          <Link to="/" className="text-sm text-stone-600 hover:text-[#005F73]">
            ← Retour à l'accueil
          </Link>
        </div>
      </div>
    </div>
  );
}
